import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, FileText, Receipt, CalendarClock, Bell, ChevronRight, MessageCircle,
  Search, ArrowUpDown, X, Eye, Download, Printer,
} from "lucide-react";
import { portalApi } from "../../context/PortalAuthContext";
import { usePortalNotifications } from "../../context/PortalNotificationsContext";
import { toast } from "sonner";
import { useWorkflowStatuses, workflowColorStyle } from "../processes/useWorkflowStatuses";
import { usePortalI18n } from "./PortalI18nContext";
import { resolveProcessStatus } from "./portalProcessStatus";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { PortalDocumentCard, PortalDocumentViewer } from "./PortalDocumentComponents";
import { PortalReceiptViewer, downloadReceipt, fmtCurrency, labelPaymentMethod, labelPaymentStatus } from "./PortalReceiptComponents";
import { usePortalSettings } from "./PortalSettingsContext";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" }); }
  catch { return iso; }
};
// AP-003 · Sempre exibimos data/hora no fuso do negócio (America/Sao_Paulo) para
// evitar deslocamento se o cliente estiver em outro fuso.
const fmtDateOnly = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "America/Sao_Paulo" }); }
  catch { return iso; }
};
const fmtTimeOnly = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" }); }
  catch { return iso; }
};

// AP-081 · Falar com JMS via WhatsApp — mesmo número oficial já usado no login
// e no menu de ajuda do Portal (+55 49 8418 3430). Mensagem pré-preenchida em pt-BR
// para o cliente iniciar a conversa em tempo real.
const WHATSAPP_JMS = "554984183430";
const openJmsWhatsApp = () => {
  const now = new Date().toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  // Emojis via codepoint p/ evitar corrupção em ferramentas de edição textual.
  const wave = String.fromCodePoint(0x1F44B); // 👋
  const cal  = String.fromCodePoint(0x1F4C5); // 📅
  const msg = encodeURIComponent(
    `Olá JMS MULTI SERVICE ${wave}\n${cal} ${now}\n\nEstou no Portal do Cliente e gostaria de tirar uma dúvida.`
  );
  window.open(`https://wa.me/${WHATSAPP_JMS}?text=${msg}`, "_blank", "noopener,noreferrer");
};

function Empty({ label, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-dashed border-border bg-white px-10 py-16 text-center">
      <p className="text-sm text-muted-foreground max-w-xl mx-auto">{label}</p>
    </div>
  );
}

// AP-104 · Contagem regressiva para o próximo agendamento
function CountdownBadge({ iso, testid = "portal-appt-countdown" }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!iso) return null;
  const target = new Date(iso);
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) {
    return (
      <span data-testid={testid} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-[11px] font-bold uppercase tracking-widest">
        ● Agora
      </span>
    );
  }
  const s = Math.floor(diff / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  let label;
  if (days > 0) label = `${days}d ${hours}h ${minutes}m`;
  else if (hours > 0) label = `${hours}h ${minutes}m ${seconds}s`;
  else label = `${minutes}m ${seconds}s`;
  const urgent = diff < 3600 * 1000; // < 1h
  const soon = diff < 24 * 3600 * 1000; // < 24h
  const cls = urgent ? "bg-rose-100 text-rose-800 animate-pulse"
                     : soon ? "bg-amber-100 text-amber-800"
                            : "bg-primary/10 text-primary";
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full ${cls} px-3 py-1 text-[11px] font-bold uppercase tracking-widest tabular-nums`}>
      ⏱ Falta {label}
    </span>
  );
}

function useFetch(path) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    let alive = true;
    portalApi.get(path).then(({ data }) => alive && setState({ data, loading: false, error: null }))
      .catch((e) => alive && setState({ data: null, loading: false, error: e }));
    return () => { alive = false; };
  }, [path]);
  return state;
}

// -------------------- DASHBOARD --------------------
export function PortalDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    const load = (silent = false) => {
      if (!silent && alive) setLoading(true);
      portalApi.get("/portal/dashboard")
        .then(({ data }) => { if (alive) setData(data); })
        .catch(() => {})
        .finally(() => { if (alive && !silent) setLoading(false); });
    };
    load();
    // AP-104 · Atualização automática (polling 30s + focus)
    const interval = setInterval(() => load(true), 30000);
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    return () => { alive = false; clearInterval(interval); window.removeEventListener("focus", onFocus); };
  }, []);
  const { byKey } = useWorkflowStatuses();
  const { t } = usePortalI18n();

  // AP-083 · Helper para localizar status do workflow. Usa a chave normalizada
  // do status; se não houver tradução na dicionário i18n, cai no rótulo original.
  const localizeStatus = (rawKey, fallbackLabel) => {
    if (!rawKey) return fallbackLabel || "—";
    const norm = String(rawKey).toLowerCase().replace(/\s+/g, "_");
    const key = `status.${norm}`;
    const translated = t(key);
    // t() devolve a própria chave quando não encontra: usamos como sinal para fallback
    return translated === key ? (fallbackLabel || rawKey) : translated;
  };

  // AP-081 · Quick Actions (Blue Edition) — 6 atalhos com tinta azul clara.
  // AP-083 · Cada card ganha uma descrição curta ("1 processo ativo", etc.).
  const _procN = data?.total_processes ?? 0;
  const _docN  = data?.documents ?? 0;
  const _recN  = data?.receipts ?? 0;
  const _apptN = data?.appointments ?? 0;
  const _notifN = (data?.notifications_recent || []).length;
  const quickActions = [
    { key: "processos",    label: t("qa.processes"),    to: "/portal/processes",     icon: FolderKanban,  emoji: "📂", value: _procN,  desc: t("qa.processesDesc",    { n: _procN  }) },
    { key: "documentos",   label: t("qa.documents"),    to: "/portal/documents",     icon: FileText,      emoji: "📄", value: _docN,   desc: t("qa.documentsDesc",    { n: _docN   }) },
    { key: "recibos",      label: t("qa.receipts"),     to: "/portal/receipts",      icon: Receipt,       emoji: "💳", value: _recN,   desc: t("qa.receiptsDesc",     { n: _recN   }) },
    { key: "agendamentos", label: t("qa.appointments"), to: "/portal/appointments",  icon: CalendarClock, emoji: "📅", value: _apptN,  desc: t("qa.appointmentsDesc", { n: _apptN  }) },
    { key: "notificacoes", label: t("qa.notifications"),to: "/portal/notifications", icon: Bell,          emoji: "🔔", value: _notifN, desc: t("qa.notifsDesc",       { n: _notifN }) },
    { key: "falar",        label: t("qa.talkNew"),      onClick: openJmsWhatsApp, external: true, icon: MessageCircle, emoji: "💬", value: null,     desc: t("qa.talkDesc") },
  ];

  const mainProc = (data?.processes_recent || [])[0];
  // AP-167 · Fonte única de status (Badge/label coerentes com Detail e Tracker).
  const mainProcResolved = mainProc ? resolveProcessStatus(mainProc, t) : null;
  const mainProcProgress = Math.max(0, Math.min(100, Number(mainProc?.progress ?? 0)));
  // AP-103 FINAL · Configurações do tenant. O botão de pagamento MP saiu do
  // Dashboard e vive somente no detalhe do processo (PortalProcessDetail).
  // O Dashboard usa apenas `portal_welcome_message` + filtro de quickActions.
  const { settings } = usePortalSettings();
  const welcomeMessage = (settings?.portal_welcome_message || "").trim();

  // AP-103 FINAL · Filtra os quick actions conforme flags do tenant.
  const filteredQuickActions = quickActions.filter((a) => {
    if (a.key === "documentos"   && settings.portal_show_documents === false)     return false;
    if (a.key === "recibos"      && settings.portal_show_receipts === false)      return false;
    if (a.key === "agendamentos" && settings.portal_show_appointments === false)  return false;
    if (a.key === "notificacoes" && settings.portal_show_notifications === false) return false;
    if (a.key === "falar"        && settings.portal_show_whatsapp === false)      return false;
    if (a.key === "processos"    && settings.portal_show_processes === false)     return false;
    return true;
  });

  return (
    <div data-testid="portal-page-dashboard" className="space-y-6">
      <header className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-primary to-primary/70 grid place-items-center shadow-md ring-1 ring-primary/30">
          <LayoutDashboard className="h-5 w-5 text-white" strokeWidth={2.2} />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground">{t("dash.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("dash.subtitle")}</p>
        </div>
      </header>

      {/* AP-103 FINAL · Banner de boas-vindas configurado pelo Master */}
      {welcomeMessage && (
        <div
          data-testid="portal-welcome-banner"
          className="rounded-[20px] border border-primary/30 bg-primary/5 px-5 py-4 shadow-sm"
        >
          <p className="text-sm md:text-base text-foreground leading-relaxed whitespace-pre-wrap">{welcomeMessage}</p>
        </div>
      )}

      {/* AP-081/083 · Card Principal — sem barra de progresso; badge + código + data + botão */}
      {!loading && mainProc && (
        <div
          data-testid="portal-featured-process"
          className="group block rounded-[20px] overflow-hidden border border-border bg-gradient-to-br from-primary to-primary/85 text-primary-foreground p-5 md:p-6 shadow-[0_10px_30px_-12px_rgba(37,99,235,0.45)] transition-all duration-200"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-widest text-white/70">{t("dash.featuredTitle")}</p>
              <p className="mt-1 text-lg md:text-xl font-display font-bold truncate max-w-[520px]">{mainProc.service_name || t("dash.featuredTitle")}</p>
            </div>
            <span
              data-testid="portal-featured-status"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[11px] font-semibold"
            >
              <span className={`inline-block h-2 w-2 rounded-full ${mainProcResolved?.tone?.dot || "bg-white"}`} />
              {mainProcResolved?.label}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-white/85">
            <div><span className="text-white/60 uppercase tracking-widest text-[10px] block mb-0.5">Código</span><span className="font-mono">{mainProc.process_number}</span></div>
            <div><span className="text-white/60 uppercase tracking-widest text-[10px] block mb-0.5">{t("dash.lastUpdate")}</span><span className="tabular-nums">{fmtDate(mainProc.last_update)}</span></div>
          </div>
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <Link
              to={`/portal/processes/${mainProc.id}`}
              data-testid="portal-featured-view"
              className="inline-flex items-center gap-2 rounded-full bg-white text-primary font-semibold text-sm px-4 py-2 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all"
            >
              {t("qa.viewDetails")}
              <ChevronRight className="h-4 w-4" />
            </Link>
            {/* AP-103 FINAL · Botão de pagamento movido para a tela de detalhes
                do processo (PortalProcessDetail). O Dashboard fica limpo. */}
          </div>
        </div>
      )}

      {/* AP-104 · Card de próximo agendamento com contagem regressiva */}
      {!loading && data?.next_appointment && settings.portal_show_appointments !== false && (
        <Link
          to="/portal/appointments"
          data-testid="portal-next-appointment"
          className="group block rounded-[20px] overflow-hidden border border-primary/30 bg-white p-5 md:p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-12 w-12 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center shrink-0">
                <CalendarClock className="h-6 w-6 text-primary" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Próximo agendamento</p>
                <p className="mt-0.5 text-base md:text-lg font-display font-bold text-foreground truncate max-w-[420px]" data-testid="portal-next-appt-title">
                  {data.next_appointment.service_name || "Agendamento"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5" data-testid="portal-next-appt-when">
                  {fmtDateTime(data.next_appointment.start_at)}
                  {data.next_appointment.staff_name ? ` · com ${data.next_appointment.staff_name}` : ""}
                </p>
                {data.next_appointment.location && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 truncate max-w-[420px]">📍 {data.next_appointment.location}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <CountdownBadge iso={data.next_appointment.start_at} testid="portal-next-appt-countdown" />
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest">
                {data.next_appointment.status === "confirmed" ? "✓ Confirmado" : "Agendado"}
              </span>
            </div>
          </div>
        </Link>
      )}

      {/* AP-081 · Quick Actions · 6 cards azul-claros */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {filteredQuickActions.map(({ key, label, to, onClick, external, icon: Icon, emoji, value, desc }) => {
          const inner = (
            <>
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary border border-primary/20 shadow-sm">
                <Icon className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <p className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                <span aria-hidden="true">{emoji}</span>
                <span className="truncate">{label}</span>
              </p>
              {value !== null ? (
                <p className="text-2xl font-bold tabular-nums text-foreground mt-0.5" data-testid={`portal-card-${key}-value`}>
                  {loading ? "…" : value}
                </p>
              ) : (
                <p className="text-sm font-semibold text-primary mt-1 flex items-center gap-1">
                  <span aria-hidden="true" className="text-emerald-500">●</span> {t("qa.talkNew")}
                </p>
              )}
              {/* AP-083 · Descrição curta */}
              <p className="text-[11px] text-muted-foreground mt-0.5 truncate" data-testid={`portal-card-${key}-desc`}>{desc}</p>
              <ChevronRight className="absolute top-4 right-4 h-4 w-4 text-primary/60 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
            </>
          );
          const cls = "group relative overflow-hidden rounded-[20px] border border-border bg-white p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 text-left";
          if (onClick) {
            return (
              <button key={key} type="button" onClick={onClick} data-testid={`portal-card-${key}`} className={cls}>{inner}</button>
            );
          }
          return external
            ? <a key={key} href={to} data-testid={`portal-card-${key}`} className={cls}>{inner}</a>
            : <Link key={key} to={to} data-testid={`portal-card-${key}`} className={cls}>{inner}</Link>;
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-[20px] border border-border bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-foreground mb-3">{t("dash.recentProcesses")}</h2>
          {loading && <p className="text-xs text-muted-foreground">{t("dash.loading")}</p>}
          {!loading && (data?.processes_recent?.length ? (
            <ul className="space-y-2">
              {data.processes_recent.map((p) => {
                const r = resolveProcessStatus(p, t);
                return (
                  <li key={p.id}>
                    <Link to={`/portal/processes/${p.id}`}
                          data-testid={`portal-dash-process-${p.id}`}
                          className="flex items-center justify-between gap-3 rounded-xl border border-border hover:bg-primary/5 hover:border-primary/30 hover:shadow-sm px-3 py-2.5 transition-all duration-150 active:scale-[0.99]">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{p.service_name || p.process_number}</p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {p.process_number} · {t("dash.lastUpdate").toLowerCase()} {fmtDate(p.last_update)}
                        </p>
                      </div>
                      <Badge className={`border ${r.tone.bg}`} variant="outline">
                        <span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${r.tone.dot}`} />
                        {r.label}
                      </Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : <p className="text-sm text-muted-foreground">{t("dash.noProcesses")}</p>)}
        </div>

        <div className="rounded-[18px] border border-border bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-foreground mb-3">{t("dash.recentNotifs")}</h2>
          {loading && <p className="text-xs text-muted-foreground">{t("common.loading")}</p>}
          {!loading && (data?.notifications_recent?.length ? (
            <ul className="space-y-2">
              {data.notifications_recent.map((n) => (
                <li key={n.id} className="rounded-xl border border-border px-3 py-2.5 hover:bg-muted/30 transition-colors">
                  <p className="text-sm">{n.description}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {n.process_number ? `${n.process_number} · ` : ""}{fmtDateTime(n.occurred_at)}
                  </p>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">{t("dash.noNotifs")}</p>)}
        </div>
      </div>
    </div>
  );
}

// -------------------- MEUS PROCESSOS --------------------
export function PortalProcesses() {
  const { t } = usePortalI18n();
  const { data, loading } = useFetch("/portal/processes");
  const { byKey } = useWorkflowStatuses();
  const items = data?.items || [];

  return (
    <div data-testid="portal-page-processes" className="space-y-6">
      <header className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center">
          <FolderKanban className="h-5 w-5 text-primary" strokeWidth={2.2} />
        </div>
        <div>
          <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight text-foreground">{t("processes.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("processes.subtitle")}</p>
        </div>
      </header>

      {loading && <Empty testid="portal-processes-loading" label={t("processes.loading")} />}
      {!loading && !items.length && <Empty testid="portal-processes-empty" label={t("processes.empty")} />}
      {!loading && items.length > 0 && (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="portal-processes-list">
          {items.map((p) => {
            const st = byKey(p.status);
            const style = workflowColorStyle(st.color);
            return (
              <li key={p.id}>
                <Link
                  to={`/portal/processes/${p.id}`}
                  data-testid={`portal-process-card-${p.id}`}
                  className="block rounded-2xl border border-border bg-white p-5 hover:shadow-sm hover:border-primary/40 transition"
                >
                  <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <span className="font-mono text-xs text-muted-foreground">{p.process_number}</span>
                    <Badge className={`border ${style.bg}`} variant="outline">
                      <span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${style.dot}`} />
                      {st.label}
                    </Badge>
                  </div>
                  <p className="text-base font-semibold text-foreground truncate">{p.service_name || "—"}</p>
                  {/* AP-073 · Removido o percentual "Progresso NN%" do cliente. */}
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                    <span>Aberto em {fmtDate(p.opening_date)}</span>
                    <span className="text-right">Atualizado {fmtDate(p.last_update)}</span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// -------------------- MEUS DOCUMENTOS --------------------
const SORT_OPTIONS = [
  { value: "recent",     label: "Mais recentes" },
  { value: "oldest",     label: "Mais antigos" },
  { value: "name_asc",   label: "Nome (A → Z)" },
  { value: "name_desc",  label: "Nome (Z → A)" },
];

function CorrectionsBanner() {
  const { t } = usePortalI18n();
  const [items, setItems] = useState([]);
  useEffect(() => {
    portalApi.get("/portal/requirements/corrections").then(({ data }) => setItems(data?.items || [])).catch(() => setItems([]));
  }, []);
  if (!items.length) return null;
  return (
    <div className="rounded-2xl border-2 border-orange-300 bg-orange-50 p-4 space-y-2" data-testid="portal-corrections-banner">
      <p className="text-sm font-bold text-orange-900">⚠️ {t("docs.correctionBannerTitle", { n: items.length })}</p>
      <ul className="space-y-2">
        {items.map((r) => (
          <li key={r.id} className="rounded-xl bg-white border border-orange-200 p-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`portal-correction-item-${r.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{r.name}</p>
              <p className="text-[11px] text-muted-foreground">{t("docs.correctionBannerProc", { num: r.process_number || "—" })}</p>
              <p className="text-xs text-orange-800 mt-0.5">{t("docs.correctionReasonTitle")}: {r.correction_reason}</p>
            </div>
            <Link to={`/portal/processes/${r.process_id}?doc=${r.id}`}
                  className="shrink-0 text-xs font-semibold rounded-md bg-orange-600 text-white px-3 py-2 hover:bg-orange-700 transition-colors text-center"
                  data-testid={`portal-correction-resend-${r.id}`}>
              {t("docs.btnResendCorrection")}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PortalDocuments() {
  const { t } = usePortalI18n();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [fileType, setFileType] = useState("");
  const [sort, setSort] = useState("recent");
  const [data, setData] = useState({ items: [], categories: [], file_types: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [viewerDoc, setViewerDoc] = useState(null);

  // Fetch — server-side filtering + sorting
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (category) params.set("category", category);
      if (fileType) params.set("file_type", fileType);
      if (sort) params.set("sort", sort);
      try {
        const { data } = await portalApi.get(`/portal/documents?${params.toString()}`);
        if (alive) setData(data);
      } catch {
        if (alive) setData({ items: [], categories: [], file_types: [], total: 0 });
      } finally {
        if (alive) setLoading(false);
      }
    }, q ? 220 : 0); // debounce only the search input
    return () => { alive = false; clearTimeout(t); };
  }, [q, category, fileType, sort]);

  const hasFilters = q || category || fileType || sort !== "recent";
  const clearAll = () => { setQ(""); setCategory(""); setFileType(""); setSort("recent"); };

  return (
    <div data-testid="portal-page-documents" className="space-y-6">
      <header className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center">
          <FileText className="h-5 w-5 text-primary" strokeWidth={2.2} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight text-foreground">{t("documents.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("documents.subtitle")}
          </p>
        </div>
        <Badge variant="outline" className="text-xs" data-testid="portal-doc-count">
          {loading ? "…" : `${data.total} ${data.total === 1 ? "documento" : "documentos"}`}
        </Badge>
      </header>

      {/* AP-188 · Aviso de documentos com correção pendente */}
      <CorrectionsBanner />

      {/* Filters bar */}
      <div className="rounded-2xl border border-border bg-white p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="portal-doc-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, número ou serviço…"
            className="pl-9"
          />
        </div>

        <select
          data-testid="portal-doc-filter-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">Todas as categorias</option>
          {data.categories.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>

        <select
          data-testid="portal-doc-filter-file-type"
          value={fileType}
          onChange={(e) => setFileType(e.target.value)}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">Todos os tipos</option>
          {data.file_types.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>

        <div className="relative">
          <ArrowUpDown className="h-4 w-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <select
            data-testid="portal-doc-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {hasFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearAll}
            data-testid="portal-doc-clear-filters"
          >
            <X className="h-4 w-4 mr-1" /> Limpar
          </Button>
        )}
      </div>

      {/* List */}
      {loading && <Empty testid="portal-documents-loading" label={t("documents.loading")} />}
      {!loading && !data.items.length && (
        <Empty
          testid="portal-documents-empty"
          label={hasFilters ? t("documents.emptyFiltered") : t("documents.empty")}
        />
      )}
      {!loading && data.items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="portal-documents-list">
          {data.items.map((d) => (
            <PortalDocumentCard key={d.id} doc={d} onView={setViewerDoc} />
          ))}
        </div>
      )}

      <PortalDocumentViewer
        doc={viewerDoc}
        open={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
      />
    </div>
  );
}

// -------------------- MEUS RECIBOS --------------------
const RECEIPT_SORTS = [
  { value: "recent",       label: "Mais recentes" },
  { value: "oldest",       label: "Mais antigos" },
  { value: "amount_desc",  label: "Maior valor" },
  { value: "amount_asc",   label: "Menor valor" },
];

export function PortalReceipts() {
  const { t } = usePortalI18n();
  const { settings } = usePortalSettings();
  const showAmounts = settings?.portal_show_amounts !== false;
  const [q, setQ] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("recent");
  const [data, setData] = useState({ items: [], statuses: [], methods: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true); setError(null);
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (paymentStatus) params.set("payment_status", paymentStatus);
      if (paymentMethod) params.set("payment_method", paymentMethod);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (sort) params.set("sort", sort);
      try {
        const { data: raw } = await portalApi.get(`/portal/receipts?${params.toString()}`);
        // AP-105 · defensivo: garante todos os arrays esperados pelo render
        const safe = {
          items: Array.isArray(raw?.items) ? raw.items : [],
          statuses: Array.isArray(raw?.statuses) ? raw.statuses : [],
          methods: Array.isArray(raw?.methods) ? raw.methods : [],
          total: Number.isFinite(raw?.total) ? raw.total : (raw?.items?.length || 0),
        };
        if (alive) { setData(safe); setError(null); }
      } catch (err) {
        if (alive) {
          setData({ items: [], statuses: [], methods: [], total: 0 });
          setError(err?.response?.data?.detail || "Não foi possível carregar seus recibos.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    }, q ? 220 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [q, paymentStatus, paymentMethod, dateFrom, dateTo, sort, reloadKey]);

  const hasFilters = q || paymentStatus || paymentMethod || dateFrom || dateTo || sort !== "recent";
  const clearAll = () => {
    setQ(""); setPaymentStatus(""); setPaymentMethod("");
    setDateFrom(""); setDateTo(""); setSort("recent");
  };

  // AP-105 · estado de erro amigável — nunca deixa página branca
  if (error && !loading) {
    return (
      <div className="space-y-4" data-testid="portal-receipts-error">
        <header className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center">
            <Receipt className="h-5 w-5 text-primary" strokeWidth={2.2} />
          </div>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-display font-bold">Meus Recibos</h1>
          </div>
        </header>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
          <p className="text-sm font-semibold text-rose-800">Não foi possível carregar seus recibos.</p>
          <p className="text-xs text-rose-600 mt-1">{String(error).slice(0, 200)}</p>
          <div className="mt-4 flex justify-center gap-2">
            <Button size="sm" onClick={() => setReloadKey(k => k + 1)} data-testid="portal-receipts-retry">Tentar novamente</Button>
            <Button variant="outline" size="sm" onClick={() => window.location.href = "/portal"} data-testid="portal-receipts-back">← Voltar ao Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="portal-page-receipts" className="space-y-6">
      <header className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center">
          <Receipt className="h-5 w-5 text-primary" strokeWidth={2.2} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight text-foreground">{t("receipts.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("receipts.subtitle")}
          </p>
        </div>
        <Badge variant="outline" className="text-xs" data-testid="portal-receipt-count">
          {loading ? "…" : `${data.total} ${data.total === 1 ? "recibo" : "recibos"}`}
        </Badge>
      </header>

      {/* Filters */}
      <div className="rounded-2xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <div className="md:col-span-2">
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="portal-receipt-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por número, processo ou serviço…"
              className="pl-9"
            />
          </div>
        </div>
        <select
          data-testid="portal-receipt-filter-status"
          value={paymentStatus}
          onChange={(e) => setPaymentStatus(e.target.value)}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">Todos os status</option>
          {(data.statuses || []).map((s) => <option key={s.value} value={s.value}>{labelPaymentStatus(s.value)}</option>)}
        </select>
        <select
          data-testid="portal-receipt-filter-method"
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">Todos os métodos</option>
          {(data.methods || []).map((m) => <option key={m.value} value={m.value}>{labelPaymentMethod(m.value)}</option>)}
        </select>
        <Input
          data-testid="portal-receipt-date-from"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          title="Data inicial"
        />
        <Input
          data-testid="portal-receipt-date-to"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          title="Data final"
        />
        <div className="relative md:col-span-2 xl:col-span-1">
          <ArrowUpDown className="h-4 w-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <select
            data-testid="portal-receipt-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent pl-8 pr-3 text-sm w-full"
          >
            {RECEIPT_SORTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {hasFilters && (
          <div className="md:col-span-3 xl:col-span-6 flex justify-end">
            <Button variant="ghost" size="sm" onClick={clearAll} data-testid="portal-receipt-clear">
              <X className="h-4 w-4 mr-1" /> Limpar filtros
            </Button>
          </div>
        )}
      </div>

      {loading && <Empty testid="portal-receipts-loading" label={t("receipts.loading")} />}
      {!loading && !data.items.length && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="portal-receipts-empty">
          <Receipt className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-700">📄 Nenhum recibo disponível.</p>
          <p className="text-xs text-slate-500 mt-1">{hasFilters ? "Nenhum recibo com os filtros aplicados." : "Assim que houver recibos, eles aparecerão aqui."}</p>
          <div className="mt-3 flex justify-center gap-2">
            {hasFilters && <Button variant="outline" size="sm" onClick={clearAll} data-testid="portal-receipts-clear">Limpar filtros</Button>}
            <Button variant="outline" size="sm" onClick={() => window.location.href = "/portal"} data-testid="portal-receipts-back-dashboard">← Voltar ao Dashboard</Button>
          </div>
        </div>
      )}
      {!loading && data.items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="portal-receipts-list">
          {data.items.map((r) => (
            <PortalReceiptCard key={r.id} receipt={r} onView={setViewer} showAmounts={showAmounts} />
          ))}
        </div>
      )}

      <PortalReceiptViewer
        receipt={viewer}
        open={!!viewer}
        onClose={() => setViewer(null)}
        showAmounts={showAmounts}
      />
    </div>
  );
}

function PortalReceiptCard({ receipt, onView, showAmounts = true }) {
  return (
    <div
      data-testid={`portal-receipt-card-${receipt.id}`}
      className="rounded-2xl border border-border bg-white p-4 flex flex-col gap-3 transition hover:border-primary/40 hover:shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-mono font-semibold text-foreground">{receipt.receipt_number}</p>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
              {labelPaymentStatus(receipt.payment_status)}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate" title={receipt.service_name}>
            {receipt.service_name || "—"}
          </p>
          {receipt.process_number && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Processo: <span className="font-mono">{receipt.process_number}</span>
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          {showAmounts ? (
            <p className="text-base font-semibold tabular-nums text-foreground" data-testid={`portal-receipt-amount-${receipt.id}`}>
              {fmtCurrency(receipt.amount, receipt.currency)}
            </p>
          ) : (
            <p className="text-[11px] italic text-muted-foreground" data-testid={`portal-receipt-amount-hidden-${receipt.id}`}>
              Valores ocultos
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            {fmtDate(receipt.payment_date || receipt.created_at)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
            {labelPaymentMethod(receipt.payment_method)}
          </p>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => onView(receipt)}
          data-testid={`portal-receipt-view-${receipt.id}`}
        >
          <Eye className="h-4 w-4 mr-1" /> Visualizar
        </Button>
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => downloadReceipt(receipt)}
          data-testid={`portal-receipt-dl-${receipt.id}`}
        >
          <Download className="h-4 w-4 mr-1" /> Baixar
        </Button>
        <Button
          type="button" size="sm"
          onClick={() => onView(receipt)}
          data-testid={`portal-receipt-print-${receipt.id}`}
          title="Abrir para imprimir"
        >
          <Printer className="h-4 w-4 mr-1" /> Imprimir
        </Button>
      </div>
    </div>
  );
}

// -------------------- MEUS AGENDAMENTOS --------------------
// -------------------- AGENDAMENTOS (com wizard AP-080) --------------------
const APPT_STATUS_LABEL = {
  scheduled:   { label: "Aguardando",  tone: "bg-amber-50 text-amber-700 border-amber-200" },
  confirmed:   { label: "Confirmado",  tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  in_progress: { label: "Em andamento",tone: "bg-sky-50 text-sky-700 border-sky-200" },
  done:        { label: "Finalizado",  tone: "bg-slate-100 text-slate-700 border-slate-200" },
  cancelled:   { label: "Cancelado",   tone: "bg-rose-50 text-rose-700 border-rose-200" },
  no_show:     { label: "Não compareceu", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  rescheduled: { label: "Reagendado",  tone: "bg-violet-50 text-violet-700 border-violet-200" },
};
const StatusBadge = ({ status }) => {
  const m = APPT_STATUS_LABEL[status] || { label: status || "—", tone: "bg-slate-100 text-slate-700 border-slate-200" };
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${m.tone}`}>{m.label}</span>;
};
const todayYMD = () => new Date().toISOString().slice(0, 10);

function BookingWizard({ open, onClose, onCreated }) {
  const { t } = usePortalI18n();
  // ETAPA 0 · Etapa opcional inicial (0 = processo). Somente aparece se o
  // cliente tiver >1 processo ATIVO. Total de etapas exibido: 5 (numeração
  // dinâmica ignora a etapa 0 quando ela não é necessária).
  const [step, setStep] = useState(1);      // 0=processo · 1=serviço · 2=funcionário · 3=data · 4=hora · 5=confirmar
  const [processes, setProcesses] = useState([]);   // apenas ATIVOS
  const [processesLoaded, setProcessesLoaded] = useState(false);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState({ process: null, service: null, variant: null, staff: null, date: todayYMD(), slot: null, notes: "" });
  // AP-004 · verificação imediata de disponibilidade após seleção do funcionário
  const [probing, setProbing] = useState(false);
  const [unavailableStaff, setUnavailableStaff] = useState(null); // { name } quando indisponível
  // AP-002 UX · scroll suave até o painel informativo assim que o serviço for selecionado
  const infoPanelRef = useRef(null);

  // ETAPA 0 · Filtra processos ATIVOS (não finalizado / não cancelado / não encerrado)
  const isActiveProcess = (p) => {
    const ps = p?.portal_status || "Ativo";
    const st = (p?.status || "").toLowerCase();
    if (ps === "Encerrado") return false;
    if (["completed", "cancelled", "concluido", "concluído", "cancelado", "finalizado", "encerrado"].includes(st)) return false;
    return true;
  };

  useEffect(() => {
    if (!open) return;
    setStep(1); setErr("");
    setSel({ process: null, service: null, variant: null, staff: null, date: todayYMD(), slot: null, notes: "" });
    setProcessesLoaded(false);
    // Carrega serviços e processos em paralelo
    portalApi.get("/portal/appointments/services")
      .then(({ data }) => setServices(data.items || []))
      .catch(() => setServices([]));
    portalApi.get("/portal/processes")
      .then(({ data }) => {
        const active = (data.items || []).filter(isActiveProcess);
        setProcesses(active);
        // Regra ETAPA 0:
        //  · 1 processo ativo → auto-seleciona silenciosamente e mantém step=1
        //  · >1 processo ativo → força passo 0 para o cliente escolher
        //  · 0 processo ativo → segue direto (sem vinculação)
        if (active.length === 1) {
          setSel((x) => ({ ...x, process: active[0] }));
        } else if (active.length > 1) {
          setStep(0);
        }
      })
      .catch(() => { setProcesses([]); })
      .finally(() => setProcessesLoaded(true));
  }, [open]);

  // AP-002 UX · sempre que um novo serviço for escolhido na Etapa 1, rola suavemente
  // o painel "Informação Importante sobre os Agendamentos JMS" até o centro da área
  // visível — funciona em desktop, tablet e mobile.
  useEffect(() => {
    if (!sel.service || step !== 1) return;
    const t = setTimeout(() => {
      const el = infoPanelRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.service?.id]);

  useEffect(() => {
    if (!sel.service) return;
    portalApi.get(`/portal/appointments/staff?service_id=${sel.service.id}`)
      .then(({ data }) => setStaff(data.items || []))
      .catch(() => setStaff([]));
  }, [sel.service]);

  useEffect(() => {
    if (!sel.staff || !sel.date) return;
    const duration = sel.variant === "extended" ? 30 : 10;
    portalApi.get(`/portal/appointments/slots?staff_id=${sel.staff.id}&date=${sel.date}&duration_min=${duration}`)
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]));
  }, [sel.staff, sel.date, sel.variant]);

  const confirm = async () => {
    setBusy(true); setErr("");
    try {
      const duration = sel.variant === "extended" ? 30 : 10;
      const prefix = sel.variant === "extended" ? "[Konsiltasyon Pwolonje · +US$50] " : "";
      const { data } = await portalApi.post("/portal/appointments", {
        service_id: sel.service.id,
        staff_id: sel.staff.id,
        start_at: sel.slot.start_at,
        duration_min: duration,
        notes: (prefix + (sel.notes || "")).trim(),
        // ETAPA 0 · vincula processo (quando cliente possui 1+ processos ativos)
        process_id: sel.process?.id || null,
      });
      onCreated?.(data.appointment);
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Não foi possível confirmar o agendamento.");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" data-testid="portal-book-wizard" onClick={onClose}>
      {/* AP-004 · Modal "Funcionário indisponível" — sobreposto ao wizard */}
      {unavailableStaff && (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 grid place-items-center p-4 animate-in fade-in-0 duration-150"
             data-testid="portal-book-unavailable-modal" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-2xl border border-amber-200 shadow-2xl w-full max-w-md p-5 space-y-3 animate-in zoom-in-95 duration-150" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-100 border border-amber-300 grid place-items-center text-lg shrink-0">⚠️</div>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-amber-900" data-testid="portal-book-unavailable-title">Funcionário indisponível</h3>
                <p className="text-sm text-slate-700 mt-1 leading-relaxed" data-testid="portal-book-unavailable-msg">
                  <b>{unavailableStaff.name}</b> não está disponível para atendimento neste momento.<br/>
                  Por favor, selecione outro funcionário para continuar.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button data-testid="portal-book-unavailable-cancel"
                onClick={(e) => { e.stopPropagation(); setUnavailableStaff(null); onClose?.(); }}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700">
                ❌ Cancelar
              </button>
              <button data-testid="portal-book-unavailable-pick-other"
                onClick={(e) => { e.stopPropagation(); setUnavailableStaff(null); setSel((x) => ({ ...x, staff: null, slot: null })); setStep(2); }}
                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-primary text-primary-foreground hover:opacity-90">
                ✅ Escolher outro funcionário
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-border shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in-0 zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Novo Agendamento · Etapa {step === 0 ? 1 : step + (processes.length > 1 ? 1 : 0)} de {5 + (processes.length > 1 ? 1 : 0)}</p>
            <p className="text-sm font-semibold">
              {step === 0 && t("book.stepProcess")}
              {step === 1 && "Escolha o serviço"}
              {step === 2 && "Escolha o profissional"}
              {step === 3 && "Escolha a data"}
              {step === 4 && "Escolha o horário"}
              {step === 5 && "Confirmar"}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100" data-testid="portal-book-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-2">
          {err && <p className="text-xs text-rose-700 rounded-md bg-rose-50 border border-rose-200 px-2 py-1.5" data-testid="portal-book-error">{err}</p>}

          {step === 0 && (
            <div data-testid="portal-book-step-process" className="space-y-3">
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
                <p className="text-sm font-bold text-primary tracking-tight">📂 {t("book.processTitle")}</p>
                <p className="text-xs text-slate-700 mt-1 leading-relaxed">{t("book.processSubtitle")}</p>
              </div>
              {!processesLoaded && (
                <p className="text-xs text-slate-400 italic">{t("appointments.loading")}</p>
              )}
              {processesLoaded && processes.length === 0 && (
                <p className="text-xs text-slate-500 italic" data-testid="portal-book-process-empty">{t("book.processNone")}</p>
              )}
              {processesLoaded && processes.map((p) => {
                const active = sel.process?.id === p.id;
                return (
                  <button key={p.id} data-testid={`portal-book-process-${p.id}`}
                    onClick={() => { setSel((x) => ({ ...x, process: p })); setStep(1); }}
                    className={`w-full text-left rounded-xl border-2 px-3.5 py-3 transition ${active ? "border-primary bg-primary/5" : "border-slate-200 hover:bg-slate-50"}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold truncate">{p.service_name || "Processo"}</span>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700">{p.process_number || p.portal_code || "—"}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {t("book.processStage")}: <b className="text-slate-700">{p.workflow_stage || "—"}</b>
                      {p.responsible_name ? <> · <span>{p.responsible_name}</span></> : null}
                    </p>
                  </button>
                );
              })}
            </div>
          )}

          {step === 1 && (services.length === 0
            ? <p className="text-xs text-slate-400 italic">Nenhum serviço disponível.</p>
            : (<>
              {services.map((s) => (
                <button key={s.id} data-testid={`portal-book-service-${s.id}`}
                  onClick={() => setSel((x) => ({ ...x, service: s, variant: null }))}
                  className={`w-full text-left rounded-lg border px-3 py-2 transition ${sel.service?.id === s.id ? "border-primary bg-primary/5" : "border-slate-200 hover:bg-slate-50"}`}>
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-[11px] text-slate-500">{s.category || "Serviço"}</p>
                </button>
              ))}
              {/* AP-002 · Painel informativo inline após seleção do serviço */}
              {sel.service && (
                <div ref={infoPanelRef} className="mt-3 rounded-2xl border border-primary/30 bg-primary/5 p-4 space-y-3 animate-in fade-in-0 slide-in-from-top-1 duration-200 scroll-mt-4" data-testid="portal-book-time-info">
                  <div>
                    <p className="text-sm font-bold text-primary tracking-tight">⏰ Informação importante sobre os agendamentos JMS</p>
                    <p className="text-xs text-slate-700 mt-1.5 leading-relaxed">
                      Para respeitar o horário de todos os clientes, cada agendamento padrão tem duração de até <b>10 minutos</b>.
                      Caso o seu atendimento necessite de mais tempo, escolha <b>Konsiltasyon Pwolonje</b>, que oferece atendimento de até <b>30 minutos</b>, mediante pagamento adicional de <b>US$ 50</b>.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button data-testid="portal-book-variant-standard"
                      onClick={() => { setSel((x) => ({ ...x, variant: "standard" })); setStep(2); }}
                      className={`text-left rounded-lg border-2 px-3 py-2.5 transition hover:bg-white ${sel.variant === "standard" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                      <p className="text-sm font-semibold text-emerald-700">🟢 Agendamento Padrão</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Até 10 minutos · sem custo adicional</p>
                    </button>
                    <button data-testid="portal-book-variant-extended"
                      onClick={() => { setSel((x) => ({ ...x, variant: "extended" })); setStep(2); }}
                      className={`text-left rounded-lg border-2 px-3 py-2.5 transition hover:bg-white ${sel.variant === "extended" ? "border-amber-500 bg-amber-50" : "border-slate-200 bg-white"}`}>
                      <p className="text-sm font-semibold text-amber-700">⭐ Konsiltasyon Pwolonje</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Até 30 minutos · <b>+US$ 50</b></p>
                    </button>
                  </div>
                </div>
              )}
            </>))}

          {step === 2 && (staff.length === 0
            ? <p className="text-xs text-slate-400 italic">Nenhum profissional para este serviço.</p>
            : staff.map((u) => (
              <button key={u.id} data-testid={`portal-book-staff-${u.id}`}
                disabled={probing}
                onClick={async () => {
                  // AP-004 · Verifica imediatamente a disponibilidade do funcionário
                  // varrendo os próximos 7 dias. Se nenhum dia tiver horários, abre modal
                  // "Funcionário indisponível" em vez de seguir para a Etapa 3.
                  setSel((x) => ({ ...x, staff: u }));
                  setProbing(true);
                  try {
                    const duration = sel.variant === "extended" ? 30 : 10;
                    const days = Array.from({ length: 7 }, (_, i) => {
                      const dt = new Date(); dt.setDate(dt.getDate() + i + 1);
                      return dt.toISOString().slice(0, 10);
                    });
                    const results = await Promise.all(days.map((d) =>
                      portalApi.get(`/portal/appointments/slots?staff_id=${u.id}&date=${d}&duration_min=${duration}`)
                        .then((r) => (r.data?.slots || []).length)
                        .catch(() => 0)
                    ));
                    const totalSlots = results.reduce((a, b) => a + b, 0);
                    if (totalSlots > 0) {
                      setStep(3);
                    } else {
                      setUnavailableStaff({ id: u.id, name: u.name });
                    }
                  } finally { setProbing(false); }
                }}
                className={`w-full text-left rounded-lg border px-3 py-2 flex items-center gap-2 transition ${sel.staff?.id === u.id ? "border-primary bg-primary/5" : "border-slate-200 hover:bg-slate-50"} disabled:opacity-60 disabled:cursor-progress`}>
                <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/30 grid place-items-center text-xs font-bold text-primary overflow-hidden">
                  {u.avatar_url ? <img src={u.avatar_url} alt="" className="h-full w-full object-cover" /> : (u.name || "").slice(0, 1)}
                </div>
                <div className="min-w-0"><p className="text-sm font-medium truncate">{u.name}</p><p className="text-[11px] text-slate-500 truncate">{u.role}</p></div>
                {probing && sel.staff?.id === u.id && (
                  <span className="ml-auto text-[11px] text-slate-500 shrink-0" data-testid="portal-book-staff-probing">verificando…</span>
                )}
              </button>
            )))}

          {step === 3 && (
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1">Data</label>
              <input type="date" data-testid="portal-book-date" value={sel.date} min={todayYMD()}
                onChange={(e) => setSel((x) => ({ ...x, date: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              <div className="mt-3 text-right">
                <Button data-testid="portal-book-date-next" onClick={() => setStep(4)}>Ver horários</Button>
              </div>
            </div>
          )}

          {step === 4 && (slots.length === 0
            ? <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1" data-testid="portal-book-no-slots">
                <p className="text-sm font-semibold text-amber-800">Nenhum horário disponível</p>
                <p className="text-xs text-amber-800/90 leading-relaxed">
                  <b>{sel.staff?.name || "Este funcionário"}</b> não possui horários disponíveis neste período. Escolha outra data ou selecione outro funcionário.
                </p>
                <div className="flex gap-2 pt-1">
                  <button data-testid="portal-book-no-slots-change-date" onClick={() => setStep(3)}
                    className="text-[11px] font-medium px-2.5 py-1 rounded-md border border-amber-300 bg-white hover:bg-amber-100 text-amber-800">Escolher outra data</button>
                  <button data-testid="portal-book-no-slots-change-staff" onClick={() => { setSel((x) => ({ ...x, staff: null, slot: null })); setStep(2); }}
                    className="text-[11px] font-medium px-2.5 py-1 rounded-md border border-amber-300 bg-white hover:bg-amber-100 text-amber-800">Selecionar outro funcionário</button>
                </div>
              </div>
            : <div className="grid grid-cols-4 gap-2">
                {slots.map((s) => (
                  <button key={s.start_at} data-testid={`portal-book-slot-${s.label}`}
                    onClick={() => { setSel((x) => ({ ...x, slot: s })); setStep(5); }}
                    className={`rounded-lg border px-2 py-1.5 text-xs font-medium transition ${sel.slot?.start_at === s.start_at ? "border-primary bg-primary text-primary-foreground" : "border-slate-200 hover:bg-slate-50"}`}>
                    {s.label}
                  </button>
                ))}
              </div>)}

          {step === 5 && (
            <div className="space-y-2 text-xs">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1" data-testid="portal-book-confirm-summary">
                {sel.process && (
                  <p data-testid="portal-book-confirm-process">
                    <span className="text-slate-500">Processo:</span>{" "}
                    <span className="font-medium">{sel.process.service_name || "Processo"}</span>{" "}
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600">{sel.process.process_number || sel.process.portal_code || "—"}</span>
                  </p>
                )}
                <p><span className="text-slate-500">Serviço:</span> <span className="font-medium">{sel.service?.name}</span></p>
                <p><span className="text-slate-500">Tipo:</span> <span className={`font-medium ${sel.variant === "extended" ? "text-amber-700" : "text-emerald-700"}`}>
                  {sel.variant === "extended" ? "⭐ Konsiltasyon Pwolonje" : "🟢 Agendamento Padrão"}
                </span></p>
                <p><span className="text-slate-500">Profissional:</span> <span className="font-medium">{sel.staff?.name}</span></p>
                <p data-testid="portal-book-confirm-date"><span className="text-slate-500">Data:</span> <span className="font-medium">{fmtDateOnly(sel.slot?.start_at)}</span></p>
                <p data-testid="portal-book-confirm-time"><span className="text-slate-500">Horário:</span> <span className="font-medium tabular-nums">{fmtTimeOnly(sel.slot?.start_at)} às {fmtTimeOnly(sel.slot?.end_at)}</span></p>
                <p data-testid="portal-book-confirm-duration"><span className="text-slate-500">Duração:</span> <span className="font-medium">{sel.variant === "extended" ? 30 : 10} minutos</span></p>
                {sel.variant === "extended" && (
                  <p className="mt-2 rounded-md bg-amber-100 border border-amber-300 px-2 py-1.5 text-amber-900 font-semibold" data-testid="portal-book-extra-fee">
                    💰 Valor adicional: <b>US$ 50</b> — cobrado no atendimento.
                  </p>
                )}
              </div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 block">Observações (opcional)</label>
              <textarea data-testid="portal-book-notes" rows={3} value={sel.notes}
                onChange={(e) => setSel((x) => ({ ...x, notes: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Alguma informação adicional…" />
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <Button variant="outline" data-testid="portal-book-back"
            disabled={busy || (step <= 1 && !(processes.length > 1))}
            onClick={() => {
              // ETAPA 0 · Voltar para a Etapa 0 apenas quando ela realmente existir (>1 processo).
              if (step === 1) {
                if (processes.length > 1) setStep(0);
                return;
              }
              setStep((s) => Math.max(1, s - 1));
            }}>Voltar</Button>
          {step === 5 ? (
            <Button data-testid="portal-book-confirm" disabled={busy || !sel.slot} onClick={confirm}>
              {busy ? "Confirmando…" : "Confirmar agendamento"}
            </Button>
          ) : step === 0 ? (
            <span className="text-[11px] text-slate-400">Selecione um processo para avançar</span>
          ) : (
            <span className="text-[11px] text-slate-400">Selecione uma opção para avançar</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function PortalAppointments() {
  const { t } = usePortalI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    portalApi.get("/portal/appointments")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => { if (!silent) setItems([]); })
      .finally(() => { if (!silent) setLoading(false); });
  };
  useEffect(() => { load(); }, []);
  // AP-104 · Atualização automática (polling 30s + refresh no focus)
  useEffect(() => {
    const interval = setInterval(() => load(true), 30000);
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(interval); window.removeEventListener("focus", onFocus); };
  }, []);

  const doCancel = async () => {
    if (!cancelTarget) return;
    setBusy(true);
    try {
      await portalApi.post(`/portal/appointments/${cancelTarget.id}/cancel`, { reason: cancelReason });
      setCancelTarget(null); setCancelReason("");
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Não foi possível cancelar."); }
    finally { setBusy(false); }
  };

  // AP-104 · Confirmar presença
  const doConfirm = async (a) => {
    try {
      await portalApi.post(`/portal/appointments/${a.id}/confirm`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Não foi possível confirmar."); }
  };
  // AP-104 · Solicitar remarcação
  const [reschedTarget, setReschedTarget] = useState(null);
  const [reschedReason, setReschedReason] = useState("");
  const doReschedule = async () => {
    if (!reschedTarget || !reschedReason.trim()) return;
    setBusy(true);
    try {
      await portalApi.post(`/portal/appointments/${reschedTarget.id}/request-reschedule`,
                            { reason: reschedReason });
      setReschedTarget(null); setReschedReason("");
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Não foi possível solicitar."); }
    finally { setBusy(false); }
  };
  // AP-104 · Adicionar ao Calendário (ICS)
  const downloadIcs = async (a) => {
    try {
      const { data } = await portalApi.get(`/portal/appointments/${a.id}/ics`, { responseType: "blob" });
      const blob = new Blob([data], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = `agendamento-${a.id.slice(0,8)}.ics`;
      link.click(); URL.revokeObjectURL(url);
    } catch { alert("Não foi possível baixar."); }
  };
  const openMap = (a) => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.location || "")}`, "_blank");
  const openWhats = () => openJmsWhatsApp();

  // AP-104 · Histórico de eventos por agendamento
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const openHistory = async (a) => {
    setHistoryTarget(a); setHistoryEvents([]); setHistoryLoading(true);
    try {
      const { data } = await portalApi.get(`/portal/appointments/${a.id}/history`);
      setHistoryEvents(data.history || []);
    } catch { setHistoryEvents([]); }
    finally { setHistoryLoading(false); }
  };

  // AP-104 · Abas
  const [tab, setTab] = useState("scheduled");
  const [quickFilter, setQuickFilter] = useState("all");
  const now = new Date();
  const inRange = (d, from, to) => { const dt = new Date(d); return dt >= from && dt < to; };
  const startOfDay = new Date(now); startOfDay.setHours(0,0,0,0);
  const endOfDay = new Date(startOfDay); endOfDay.setDate(endOfDay.getDate() + 1);
  const startOfWeek = new Date(startOfDay); startOfWeek.setDate(startOfDay.getDate() - startOfDay.getDay());
  const endOfWeek = new Date(startOfWeek); endOfWeek.setDate(endOfWeek.getDate() + 7);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const applyQuickFilter = (arr) => {
    if (quickFilter === "today") return arr.filter(a => inRange(a.start_at, startOfDay, endOfDay));
    if (quickFilter === "week") return arr.filter(a => inRange(a.start_at, startOfWeek, endOfWeek));
    if (quickFilter === "month") return arr.filter(a => inRange(a.start_at, startOfMonth, endOfMonth));
    if (quickFilter === "future") return arr.filter(a => new Date(a.start_at) >= now);
    return arr;
  };
  const filtered = useMemo(() => {
    let arr = items;
    if (tab === "scheduled") arr = arr.filter(a => a.status === "scheduled");
    else if (tab === "confirmed") arr = arr.filter(a => a.status === "confirmed");
    else if (tab === "done") arr = arr.filter(a => a.status === "done");
    else if (tab === "cancelled") arr = arr.filter(a => a.status === "cancelled");
    return applyQuickFilter(arr);
  }, [items, tab, quickFilter]);

  return (
    <div data-testid="portal-page-appointments" className="space-y-6">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center">
            <CalendarClock className="h-5 w-5 text-primary" strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight text-foreground">{t("appointments.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("appointments.subtitle")}</p>
          </div>
        </div>
        <Button data-testid="portal-book-open" onClick={() => setOpen(true)}>+ {t("appointments.new")}</Button>
      </header>

      {loading && <Empty testid="portal-appointments-loading" label={t("appointments.loading")} />}
      {!loading && items.length === 0 && (
        <div data-testid="portal-appointments-empty" className="rounded-[20px] border border-dashed border-primary/30 bg-primary/5 p-8 md:p-10 text-center shadow-sm">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-white border border-primary/30 grid place-items-center shadow-sm mb-4">
            <CalendarClock className="h-7 w-7 text-primary" strokeWidth={2} />
          </div>
          <p className="text-sm md:text-base text-foreground font-medium max-w-md mx-auto">{t("appointments.empty")}</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <Button data-testid="portal-appt-empty-book" onClick={() => setOpen(true)}>+ {t("appointments.new")}</Button>
            <button
              data-testid="portal-appt-empty-whatsapp"
              onClick={openJmsWhatsApp}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-white px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/5 hover:-translate-y-0.5 active:scale-[0.98] transition-all"
            >
              <MessageCircle className="h-4 w-4" /> {t("qa.talkNew")}
            </button>
          </div>
        </div>
      )}

      {!loading && (
        <>
          {/* AP-104 · Abas + quick filters */}
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2" data-testid="portal-appt-tabs">
            {[
              ["scheduled","Agendados"], ["confirmed","Confirmados"],
              ["done","Concluídos"], ["cancelled","Cancelados"]
            ].map(([k,label])=>(
              <button key={k} onClick={()=>setTab(k)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${tab===k ? "bg-primary text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid={`portal-appt-tab-${k}`}>{label}</button>
            ))}
            <span className="ml-auto text-[10px] text-slate-500 hidden md:inline">Filtro rápido:</span>
            {[
              ["all","Todos"],["today","Hoje"],["week","Semana"],
              ["month","Mês"],["future","Próximos"]
            ].map(([k,label])=>(
              <button key={k} onClick={()=>setQuickFilter(k)}
                className={`px-2 py-1 text-[10px] font-semibold rounded ${quickFilter===k ? "bg-primary/15 text-primary" : "text-slate-500 hover:bg-slate-100"}`}
                data-testid={`portal-appt-quick-${k}`}>{label}</button>
            ))}
          </div>

          <ul className="space-y-3" data-testid="portal-appointments-list">
            {filtered.length === 0 && (
              <p className="text-xs text-slate-500 italic px-2">Nenhum agendamento nesta categoria.</p>
            )}
            {filtered.map((a) => (
              <li key={a.id} className="rounded-2xl border border-border bg-white p-4" data-testid={`portal-appt-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/30 grid place-items-center shrink-0">
                    <CalendarClock className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{a.service_name || "Agendamento"}</p>
                      <StatusBadge status={a.status} />
                    </div>
                    <p className="text-xs text-muted-foreground">{fmtDateTime(a.start_at)} · {a.duration_min || 60} min{a.staff_name ? ` · com ${a.staff_name}` : ""}</p>
                    {a.location && <p className="text-[11px] text-muted-foreground mt-0.5">📍 {a.location}</p>}
                    {a.notes && <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{a.notes}</p>}
                    {a.reschedule_request?.status === "pending" && (
                      <p className="mt-1 text-[11px] font-semibold text-amber-700">⏳ Remarcação em análise</p>
                    )}
                  </div>
                </div>
                {/* AP-104 · Ações */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
                  {a.status === "scheduled" && (
                    <Button size="sm" onClick={()=>doConfirm(a)} data-testid={`portal-appt-confirm-${a.id}`}>
                      ✓ Confirmar presença
                    </Button>
                  )}
                  {["scheduled","confirmed"].includes(a.status) && !a.reschedule_request?.status && (
                    <Button variant="outline" size="sm" onClick={()=>setReschedTarget(a)} data-testid={`portal-appt-reschedule-${a.id}`}>
                      Solicitar Remarcação
                    </Button>
                  )}
                  {["scheduled","confirmed"].includes(a.status) && (
                    <Button variant="outline" size="sm" onClick={()=>downloadIcs(a)} data-testid={`portal-appt-ics-${a.id}`}>
                      📅 Adicionar ao Calendário
                    </Button>
                  )}
                  {a.location && (
                    <Button variant="outline" size="sm" onClick={()=>openMap(a)} data-testid={`portal-appt-map-${a.id}`}>
                      📍 Abrir localização
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={openWhats} data-testid={`portal-appt-whats-${a.id}`}>
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp JMS
                  </Button>
                  <Button variant="outline" size="sm" onClick={()=>openHistory(a)} data-testid={`portal-appt-history-${a.id}`}>
                    📜 Ver histórico
                  </Button>
                  {["scheduled","confirmed"].includes(a.status) && (
                    <Button variant="outline" size="sm" onClick={()=>setCancelTarget(a)} className="text-rose-700 hover:bg-rose-50" data-testid={`portal-appt-cancel-${a.id}`}>
                      Cancelar
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Dialog Solicitar Remarcação */}
      {reschedTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={()=>setReschedTarget(null)} data-testid="portal-reschedule-dialog">
          <div className="bg-white rounded-2xl p-5 max-w-md w-full" onClick={(e)=>e.stopPropagation()}>
            <p className="text-lg font-bold mb-2">Solicitar Remarcação</p>
            <p className="text-xs text-slate-500 mb-3">Informe o motivo da remarcação. A secretaria será notificada.</p>
            <textarea value={reschedReason} onChange={(e)=>setReschedReason(e.target.value)} rows={4} className="w-full border rounded-lg p-2 text-sm" placeholder="Ex.: conflito de horário no trabalho" data-testid="portal-reschedule-reason" />
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={()=>setReschedTarget(null)}>Cancelar</Button>
              <Button size="sm" onClick={doReschedule} disabled={busy || !reschedReason.trim()} data-testid="portal-reschedule-submit">Enviar solicitação</Button>
            </div>
          </div>
        </div>
      )}

      {/* AP-104 · Modal Histórico completo do agendamento */}
      {historyTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={()=>setHistoryTarget(null)} data-testid="portal-appt-history-dialog">
          <div className="bg-white rounded-2xl p-5 max-w-md w-full max-h-[80vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <p className="text-lg font-bold mb-1">Histórico do agendamento</p>
            <p className="text-xs text-slate-500 mb-4">{historyTarget.service_name || "Agendamento"} · {fmtDateTime(historyTarget.start_at)}</p>
            {historyLoading && <p className="text-xs text-slate-500 italic">Carregando eventos…</p>}
            {!historyLoading && historyEvents.length === 0 && (
              <p className="text-xs text-slate-500 italic" data-testid="portal-appt-history-empty">Nenhum evento registrado ainda.</p>
            )}
            {!historyLoading && historyEvents.length > 0 && (
              <ol className="space-y-3 border-l-2 border-primary/20 pl-4" data-testid="portal-appt-history-list">
                {historyEvents.slice().reverse().map((ev, i) => {
                  const labels = {
                    created: ["🆕", "Agendamento criado"],
                    confirmed: ["✅", "Presença confirmada"],
                    cancelled: ["❌", "Cancelado"],
                    reschedule_requested: ["⏳", "Remarcação solicitada"],
                    reschedule_approved: ["👍", "Remarcação aprovada"],
                    reschedule_rejected: ["👎", "Remarcação rejeitada"],
                    rescheduled: ["🔄", "Remarcado"],
                    updated: ["✏️", "Alterado"],
                    done: ["🏁", "Concluído"],
                    no_show: ["🚫", "Não compareceu"],
                  };
                  const [emo, label] = labels[ev.event] || ["•", ev.event || "Evento"];
                  return (
                    <li key={i} className="relative" data-testid={`portal-appt-history-item-${i}`}>
                      <span className="absolute -left-[22px] top-0 h-4 w-4 rounded-full bg-primary text-white text-[10px] grid place-items-center">{emo}</span>
                      <p className="text-sm font-semibold text-foreground">{label}</p>
                      <p className="text-[11px] text-slate-500 tabular-nums">{fmtDateTime(ev.at)}{ev.by === "client" ? " · Você" : ev.by === "admin" ? " · Equipe JMS" : ""}</p>
                      {ev.reason && <p className="text-xs text-slate-600 mt-1">💬 {ev.reason}</p>}
                      {ev.note && <p className="text-xs text-slate-600 mt-1">📝 {ev.note}</p>}
                      {ev.from && ev.to && <p className="text-[11px] text-slate-500 mt-0.5">{fmtDateTime(ev.from)} → {fmtDateTime(ev.to)}</p>}
                      {ev.fields && ev.fields.length > 0 && <p className="text-[11px] text-slate-500 mt-0.5">Campos: {ev.fields.join(", ")}</p>}
                    </li>
                  );
                })}
              </ol>
            )}
            <div className="mt-4 flex justify-end">
              <Button size="sm" variant="outline" onClick={()=>setHistoryTarget(null)} data-testid="portal-appt-history-close">Fechar</Button>
            </div>
          </div>
        </div>
      )}

      <BookingWizard open={open} onClose={() => setOpen(false)} onCreated={load} />

      {cancelTarget && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" data-testid="portal-appt-cancel-dialog" onClick={() => !busy && setCancelTarget(null)}>
          <div className="bg-white rounded-2xl border border-border shadow-2xl w-full max-w-md p-5 animate-in fade-in-0 zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-semibold">Cancelar este agendamento?</p>
            <p className="text-xs text-slate-500 mt-1">{cancelTarget.service_name || "Agendamento"} · {fmtDateTime(cancelTarget.start_at)}</p>
            <textarea rows={3} placeholder="Motivo (opcional)" value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="w-full mt-3 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="portal-appt-cancel-reason" />
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={busy} data-testid="portal-appt-cancel-abort">Voltar</Button>
              <Button className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doCancel} disabled={busy} data-testid="portal-appt-cancel-confirm">
                {busy ? "Cancelando…" : "Cancelar agendamento"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- NOTIFICAÇÕES --------------------
export function PortalNotifications() {
  const { t } = usePortalI18n();
  const navigate = useNavigate();
  // AP-167 · Estado compartilhado com o sino (badge atualiza em tempo real).
  const { markRead: ctxMarkRead, markAllRead: ctxMarkAllRead } = usePortalNotifications();
  const [tab, setTab] = React.useState("all"); // all | unread | read
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await portalApi.get("/portal/notifications",
        { params: { filter: tab } });
      setItems(data?.items || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [tab]);

  React.useEffect(() => { load(); }, [load]);

  // AP-161 · Polling 15s + reload on visibility — tempo-real aproximado.
  React.useEffect(() => {
    const iv = setInterval(load, 15000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  const markRead = async (n) => {
    if (n.is_read) return;
    // Otimista local + sincroniza badge/contexto.
    setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, is_read: true, read_at: new Date().toISOString() } : x));
    try { await ctxMarkRead(n.id); } catch { /* silent */ }
  };

  const openNotification = async (n) => {
    await markRead(n);
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    setItems((arr) => arr.map((x) => ({ ...x, is_read: true, read_at: new Date().toISOString() })));
    try {
      await ctxMarkAllRead();
      toast.success(t("notifs.toastAllRead"));
      load();
    } catch { /* silent */ }
  };

  const IconFor = ({ kind }) => {
    const k = (kind || "").toLowerCase();
    if (k.includes("payment") || k.includes("receipt")) return <span>💳</span>;
    if (k.includes("document") || k.includes("doc"))    return <span>📄</span>;
    if (k.includes("appointment"))                       return <span>📅</span>;
    if (k.includes("process"))                           return <span>📁</span>;
    if (k.includes("message") || k.includes("note"))     return <span>💬</span>;
    return <Bell className="h-4 w-4 text-primary" strokeWidth={2.2} />;
  };
  const ctaFor = (kind) => {
    const k = (kind || "").toLowerCase();
    if (k === "requirement_correction_requested" || k === "requirement_rejected") return t("notifs.ctaResend");
    if (k.includes("receipt") || k.includes("payment")) return t("notifs.ctaReceipt");
    if (k.includes("appointment"))                       return t("notifs.ctaAppointment");
    if (k.includes("document") || k.includes("doc"))    return t("notifs.ctaDocument");
    if (k.includes("process"))                           return t("notifs.ctaProcess");
    return t("notifs.ctaOpen");
  };

  const _resolveLabel = (key, fallback) => {
    const v = t(key);
    return (!v || v === key) ? fallback : v;
  };
  const TABS = [
    { key: "all",    label: _resolveLabel("notifs.tabAll",    "Todas") },
    { key: "unread", label: _resolveLabel("notifs.tabUnread", "Não lidas") },
    { key: "read",   label: _resolveLabel("notifs.tabRead",   "Lidas") },
  ];

  return (
    <div data-testid="portal-page-notifications" className="space-y-5">
      <header className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center">
          <Bell className="h-5 w-5 text-primary" strokeWidth={2.2} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight text-foreground">{t("notifs.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("notifs.subtitle")}</p>
        </div>
        <button
          data-testid="portal-notifs-mark-all"
          onClick={markAllRead}
          className="text-xs rounded-md border px-2.5 py-1.5 hover:bg-muted/50 transition-colors">
          {t("notifs.markAll")}
        </button>
      </header>

      <div className="flex gap-1 border-b" data-testid="portal-notifs-tabs">
        {TABS.map((o) => (
          <button
            key={o.key}
            data-testid={`portal-notifs-tab-${o.key}`}
            onClick={() => setTab(o.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === o.key
                ? "text-primary border-primary"
                : "text-muted-foreground border-transparent hover:text-foreground"
            }`}>
            {o.label}
          </button>
        ))}
      </div>

      {loading && <Empty testid="portal-notifications-loading" label={t("notifs.loading")} />}
      {!loading && !items.length && <Empty testid="portal-notifications-empty" label={t("notifs.empty")} />}
      {!loading && items.length > 0 && (
        <ol className="space-y-2" data-testid="portal-notifications-list">
          {items.map((n) => (
            <li key={n.id}>
              <div
                data-testid={`portal-notif-${n.id}`}
                onClick={() => openNotification(n)}
                className={`group rounded-[20px] border px-4 py-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-3 cursor-pointer ${
                  n.is_read ? "bg-slate-50/60 border-slate-200" : "bg-white border-primary/20"
                }`}>
                <div className="mt-0.5 h-9 w-9 rounded-xl bg-primary/10 border border-primary/20 grid place-items-center shrink-0 text-base">
                  <IconFor kind={n.type} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!n.is_read && (
                      <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-primary shadow-[0_0_0_3px_rgba(37,99,235,0.15)]"
                            data-testid={`portal-notif-dot-${n.id}`} />
                    )}
                    <p className={`text-sm truncate ${n.is_read ? "text-slate-700" : "font-semibold text-foreground"}`}>
                      {n.i18n_key ? t(`${n.i18n_key}.title`, n.i18n_params || {}) : (n.title || n.type || t("notifs.title"))}
                    </p>
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground leading-relaxed">
                    {n.i18n_key
                      ? t(`${n.i18n_key}.body`, n.i18n_params || {}) + (n.i18n_params?.date ? t(`${n.i18n_key}.due`, { date: new Date(n.i18n_params.date + "T12:00:00").toLocaleDateString() }) : "")
                      : n.message}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="tabular-nums">{fmtDateTime(n.occurred_at)}</span>
                    {n.process_number && <>
                      <span aria-hidden="true">·</span>
                      <span className="font-mono">{n.process_number}</span>
                    </>}
                  </div>
                </div>
                {n.link && (
                  <button
                    data-testid={`portal-notif-cta-${n.id}`}
                    onClick={(e) => { e.stopPropagation(); openNotification(n); }}
                    className="shrink-0 text-xs rounded-md bg-primary text-primary-foreground px-2.5 py-1.5 hover:bg-primary/90 self-start">
                    {ctaFor(n.type)}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
